import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, Instagram, MessageCircle, Menu, X } from "lucide-react";
import { CONTACT, NAV_LINKS } from "@/data/content";

const iconCls =
    "flex h-9 w-9 items-center justify-center rounded-full border border-ivory/15 text-ivory/70 transition-colors duration-300 hover:border-gold hover:bg-gold hover:text-night";

const QUICK_LINKS = [
    { testid: "nav-whatsapp-link", href: CONTACT.whatsappHref, icon: MessageCircle, label: "WhatsApp", external: true },
    { testid: "nav-phone-link", href: CONTACT.phoneHref, icon: Phone, label: "Call" },
    { testid: "nav-email-link", href: CONTACT.emailHref, icon: Mail, label: "Email" },
    { testid: "nav-instagram-link", href: CONTACT.instagramHref, icon: Instagram, label: "Instagram", external: true },
];

const Brand = () => (
    <Link to="/" data-testid="nav-brand-logo" className="group flex items-center gap-3">
        <img src="/brand/mark.png" alt="Noir Nest logo" data-testid="nav-brand-mark" className="h-11 w-auto transition-transform duration-500 group-hover:scale-105 lg:h-12" />
        <span className="flex items-baseline gap-2">
            <span className="font-serif text-2xl font-medium tracking-tight text-ivory">
                Noir<span className="italic text-gold">Nest</span>
            </span>
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.3em] text-ivory/40 sm:block">Kolkata</span>
        </span>
    </Link>
);

const DesktopLinks = () => (
    <nav className="hidden items-center gap-8 lg:flex">
        {NAV_LINKS.map((l) => (
            <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testid}
                className={({ isActive }) =>
                    `group relative text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:text-ivory ${isActive ? "text-ivory" : "text-ivory/55"}`
                }
            >
                {({ isActive }) => (
                    <>
                        {l.label}
                        <span className={`absolute -bottom-1 left-0 h-px bg-gold transition-all duration-300 group-hover:w-full ${isActive ? "w-full" : "w-0"}`} />
                    </>
                )}
            </NavLink>
        ))}
    </nav>
);

const QuickLinks = () => (
    <div className="hidden items-center gap-2 md:flex">
        {QUICK_LINKS.map((q) => (
            <a
                key={q.testid}
                data-testid={q.testid}
                href={q.href}
                aria-label={q.label}
                className={iconCls}
                {...(q.external ? { target: "_blank", rel: "noreferrer" } : {})}
            >
                <q.icon size={15} strokeWidth={1.5} />
            </a>
        ))}
    </div>
);

const MobileMenu = ({ onNavigate }) => (
    <motion.nav
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.35, ease: "easeInOut" }}
        className="overflow-hidden border-t border-ivory/10 bg-night/95 backdrop-blur-md lg:hidden"
    >
        <div className="flex flex-col gap-1 px-6 py-6">
            {NAV_LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                    <NavLink
                        to={l.to}
                        end={l.to === "/"}
                        data-testid={`mobile-${l.testid}`}
                        onClick={onNavigate}
                        className={({ isActive }) => `block py-2 font-serif text-2xl ${isActive ? "italic text-gold" : "text-ivory"}`}
                    >
                        {l.label}
                    </NavLink>
                </motion.div>
            ))}
        </div>
    </motion.nav>
);

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    return (
        <header data-testid="site-header" className="fixed inset-x-0 top-0 z-50 border-b border-ivory/10 bg-night/95 text-ivory backdrop-blur-md">
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 sm:px-12 lg:h-20 lg:px-20">
                <Brand />
                <DesktopLinks />
                <QuickLinks />
                <button
                    data-testid="nav-menu-toggle"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-ivory/15 text-ivory lg:hidden"
                >
                    {open ? <X size={16} /> : <Menu size={16} />}
                </button>
            </div>
            <AnimatePresence>{open && <MobileMenu onNavigate={() => setOpen(false)} />}</AnimatePresence>
        </header>
    );
};
